import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy,
  Person,
  Clear as ClearIcon,
  Circle,
  PersonAdd,
  AttachFile,
  Close,
  Science,
  LocalHospital,
  Analytics,
  Psychology,
  Support,
  Event
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const MammoAssistChat = ({ userRole, portalContext, compact = false, connectionStatus = {} }) => {
  const { user, getTokenForChatbot } = useAuth();
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      text: `Hello ${user?.first_name || 'there'}! I'm MammoAssist, your AI companion for breast health support. 

🔹 **Multi-user chat**: Click the person+ icon to invite others
🔹 **AI agents**: Click the + icon to add specialized agents  
🔹 **File attachments**: Click the 📎 icon to attach files

How can I help you today?`,
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [addUserMenuAnchor, setAddUserMenuAnchor] = useState(null);
  const [addAgentMenuAnchor, setAddAgentMenuAnchor] = useState(null);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [chatParticipants, setChatParticipants] = useState([]);
  const [activeAgents, setActiveAgents] = useState(['MammoAssist']);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getConnectionStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success.main';
      case 'error': return 'error.main';
      default: return 'grey.400';
    }
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    setAttachedFiles(prev => [...prev, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddUser = (userType) => {
    // In real implementation, this would open a user selection dialog
    const newParticipant = {
      id: Date.now(),
      name: `${userType} User`,
      type: userType,
      avatar: userType === 'doctor' ? '👨‍⚕️' : userType === 'nurse' ? '👩‍⚕️' : '👤'
    };
    setChatParticipants(prev => [...prev, newParticipant]);
    setAddUserMenuAnchor(null);
  };

  const handleAddAgent = (agentType) => {
    if (!activeAgents.includes(agentType)) {
      setActiveAgents(prev => [...prev, agentType]);
      
      // Add agent introduction message
      const agentMessages = {
        'LabTestsAgent': 'Lab Tests Agent has joined the chat. I can help with laboratory test interpretations and recommendations.',
        'DiagnosticAgent': 'Diagnostic Agent has joined the chat. I specialize in medical imaging and diagnostic procedures.',
        'TreatmentAgent': 'Treatment Planning Agent has joined the chat. I can assist with treatment options and protocols.',
        'SupportAgent': 'Patient Support Agent has joined the chat. I\'m here to provide emotional and practical support.',
        'SchedulingAgent': 'Scheduling Agent has joined the chat. I can help coordinate appointments and procedures.'
      };

      const agentMessage = {
        id: Date.now(),
        type: 'ai',
        text: agentMessages[agentType] || `${agentType} has joined the chat.`,
        timestamp: new Date(),
        agent: agentType
      };

      setMessages(prev => [...prev, agentMessage]);
    }
    setAddAgentMenuAnchor(null);
  };

  const removeParticipant = (participantId) => {
    setChatParticipants(prev => prev.filter(p => p.id !== participantId));
  };

  const removeAgent = (agentType) => {
    if (agentType !== 'MammoAssist') { // Don't allow removing the main agent
      setActiveAgents(prev => prev.filter(a => a !== agentType));
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Get JWT token from Hasura GraphQL API (primary auth service)
      // Hasura generates and manages all JWT tokens for the application
      const token = await getTokenForChatbot();
      
      // FastAPI serves ONLY the MammoAssist chatbot functionality
      // It receives the JWT token from Hasura and validates it for session context
      const response = await fetch('http://localhost:8007/api/chat/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // JWT from Hasura GraphQL API
        },
        body: JSON.stringify({
          message: userMessage.text,
          context: {
            portal: portalContext,
            user_role: userRole,
            user_id: user?.id,
            conversation_id: `${user?.id}_${portalContext}_${Date.now()}`,
            // FastAPI uses this context + JWT claims for personalized responses
            session_data: {
              hasura_user_id: user?.id,
              hasura_role: user?.primary_role,
              portal_context: portalContext
            }
          }
        })
      });

      if (!response.ok) throw new Error('Failed to get AI response');

      const data = await response.json();
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: data.response || 'I apologize, but I encountered an issue processing your request. Please try again.',
        timestamp: new Date(),
        confidence: data.confidence,
        sources: data.sources
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        text: 'I apologize, but I\'m currently experiencing technical difficulties. Please try again in a moment.',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([
      {
        id: 1,
        type: 'ai',
        text: `Hello ${user?.first_name || 'there'}! I'm MammoAssist, your AI companion for breast health support. How can I help you today?`,
        timestamp: new Date()
      }
    ]);
  };

  const MessageBubble = ({ message }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        mb: 1,
        gap: 1
      }}
    >
      <Avatar
        sx={{
          width: 24,
          height: 24,
          bgcolor: message.type === 'user' ? 'primary.main' : 'secondary.main'
        }}
      >
        {message.type === 'user' ? <Person sx={{ fontSize: 14 }} /> : <SmartToy sx={{ fontSize: 14 }} />}
      </Avatar>
      
      <Paper
        sx={{
          p: 1.5,
          maxWidth: '80%',
          bgcolor: message.type === 'user' ? 'primary.light' : 'grey.100',
          color: message.type === 'user' ? 'primary.contrastText' : 'text.primary',
          borderRadius: 2,
          ...(message.isError && { bgcolor: 'error.light', color: 'error.contrastText' })
        }}
      >
        <Typography variant="body2" sx={{ fontSize: compact ? '0.75rem' : '0.85rem' }}>
          {message.text}
        </Typography>
        
        {message.confidence && (
          <Chip
            label={`Confidence: ${Math.round(message.confidence * 100)}%`}
            size="small"
            variant="outlined"
            sx={{ mt: 0.5, height: 16, fontSize: '0.65rem' }}
          />
        )}
        
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            mt: 0.5, 
            opacity: 0.7, 
            fontSize: compact ? '0.6rem' : '0.7rem' 
          }}
        >
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>
      </Paper>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        height: compact ? 300 : 500, 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'background.paper'
      }}
    >
      {/* Chat Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        p: 1,
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 20, height: 20, bgcolor: 'secondary.main' }}>
            <SmartToy sx={{ fontSize: 12 }} />
          </Avatar>
          <Typography variant="subtitle2" sx={{ fontSize: compact ? '0.75rem' : '0.85rem' }}>
            MammoAssist AI
          </Typography>
          <Chip 
            label={portalContext} 
            size="small" 
            color="primary" 
            variant="outlined"
            sx={{ height: 16, fontSize: '0.6rem' }}
          />
          
          {/* Active Agents Display */}
          {activeAgents.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {activeAgents.slice(1).map((agent, index) => (
                <Chip
                  key={agent}
                  label={agent.replace('Agent', '')}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  deleteIcon={<Close sx={{ fontSize: 12 }} />}
                  onDelete={() => removeAgent(agent)}
                  sx={{ height: 16, fontSize: '0.5rem' }}
                />
              ))}
            </Box>
          )}

          {/* Chat Participants Display */}
          {user && chatParticipants.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {chatParticipants.map((participant) => (
                <Chip
                  key={participant.id}
                  label={participant.name}
                  size="small"
                  color="info"
                  variant="outlined"
                  deleteIcon={<Close sx={{ fontSize: 12 }} />}
                  onDelete={() => removeParticipant(participant.id)}
                  sx={{ height: 16, fontSize: '0.5rem' }}
                />
              ))}
            </Box>
          )}
          
          {/* Connection Status Indicators */}
          {!compact && connectionStatus && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Circle sx={{ 
                  fontSize: 6, 
                  color: getConnectionStatusColor(connectionStatus.fastapi) 
                }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  API
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Circle sx={{ 
                  fontSize: 6, 
                  color: getConnectionStatusColor(connectionStatus.mcp) 
                }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                  MCP
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {/* Add User/Agent Buttons */}
          {user ? (
            // Logged in: Add other users
            <Tooltip title="Add user to chat">
              <IconButton 
                size="small" 
                onClick={(e) => setAddUserMenuAnchor(e.currentTarget)}
                aria-label="Add user to chat"
              >
                <PersonAdd sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          ) : (
            // Not logged in: Add AI agents
            <Tooltip title="Add AI agent">
              <IconButton 
                size="small" 
                onClick={(e) => setAddAgentMenuAnchor(e.currentTarget)}
                aria-label="Add AI agent to chat"
              >
                <SmartToy sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
          
          <IconButton 
            size="small" 
            onClick={clearChat} 
            title="Clear chat"
            aria-label="Clear chat history"
          >
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 1,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
        
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
              <SmartToy sx={{ fontSize: 14 }} />
            </Avatar>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress 
                size={16} 
                role="progressbar"
                aria-label="MammoAssist is processing your message"
                title="Loading response"
              />
              <Typography variant="caption" color="text.secondary">
                MammoAssist is thinking...
              </Typography>
            </Box>
          </Box>
        )}
        
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      {/* Input Section */}
      <Box sx={{ p: 1, borderTop: 1, borderColor: 'divider' }}>
        {/* File Attachments Display */}
        {attachedFiles.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
              Attachments:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {attachedFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.name.length > 20 ? `${file.name.substring(0, 17)}...` : file.name}
                  size="small"
                  variant="outlined"
                  deleteIcon={<Close sx={{ fontSize: 12 }} />}
                  onDelete={() => removeAttachment(index)}
                  sx={{ 
                    height: 20, 
                    fontSize: '0.6rem',
                    '& .MuiChip-label': { px: 0.5 }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
          {/* File Attachment Button */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileAttachment}
            accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.csv,.xlsx,.xls"
          />
          <Tooltip title="Attach files (PDF, DOC, images, CSV, etc.)">
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              sx={{ 
                mb: 0.25,
                color: attachedFiles.length > 0 ? 'primary.main' : 'text.secondary',
                '&:hover': { color: 'primary.main' }
              }}
              aria-label="Attach files"
            >
              <AttachFile sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          
          <TextField
            fullWidth
            size="small"
            placeholder="Ask about breast health, trials, or support..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
            multiline
            maxRows={compact ? 2 : 3}
            aria-label="Message input for MammoAssist chat"
            title="Type your message here"
            sx={{
              '& .MuiInputBase-root': {
                fontSize: compact ? '0.75rem' : '0.85rem'
              }
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            title="Send message"
            aria-label="Send message to MammoAssist"
            sx={{ 
              minWidth: 'auto',
              px: 1
            }}
          >
            <SendIcon sx={{ fontSize: 16 }} />
          </Button>
        </Box>
      </Box>

      {/* Add User Menu */}
      <Menu
        anchorEl={addUserMenuAnchor}
        open={Boolean(addUserMenuAnchor)}
        onClose={() => setAddUserMenuAnchor(null)}
        sx={{ mt: 1 }}
      >
        <MenuItem onClick={() => handleAddUser('Dr. Sarah Johnson')}>
          Dr. Sarah Johnson
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('Nurse Mary Chen')}>
          Nurse Mary Chen
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('Support Specialist Alex')}>
          Support Specialist Alex
        </MenuItem>
        <MenuItem onClick={() => setAddUserMenuAnchor(null)}>
          <Typography variant="caption" color="text.secondary">
            More users available in full version
          </Typography>
        </MenuItem>
      </Menu>

      {/* Add Agent Menu */}
      <Menu
        anchorEl={addAgentMenuAnchor}
        open={Boolean(addAgentMenuAnchor)}
        onClose={() => setAddAgentMenuAnchor(null)}
        sx={{ mt: 1 }}
      >
        <MenuItem onClick={() => handleAddAgent('Lab Tests Agent')}>
          <SmartToy sx={{ fontSize: 16, mr: 1 }} />
          Lab Tests Agent
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('Nutrition Agent')}>
          <SmartToy sx={{ fontSize: 16, mr: 1 }} />
          Nutrition Agent
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('Exercise Agent')}>
          <SmartToy sx={{ fontSize: 16, mr: 1 }} />
          Exercise Agent
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('Mental Health Agent')}>
          <SmartToy sx={{ fontSize: 16, mr: 1 }} />
          Mental Health Agent
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('Insurance Agent')}>
          <SmartToy sx={{ fontSize: 16, mr: 1 }} />
          Insurance Agent
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default MammoAssistChat;